// TVPairingPage.jsx  (src/pages/tv/TVPairingPage.jsx)

import React, { useRef, useState, useEffect } from "react";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
const CODE_LENGTH = 6;

export default function TVPairingPage() {
  const [digits, setDigits] = useState(Array(CODE_LENGTH).fill(""));
  const [status, setStatus] = useState("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const [mounted, setMounted] = useState(false);
  const inputRefs = useRef([]);

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 60);
    // Inject fonts + keyframes once
    const el = document.createElement("style");
    el.textContent = `
      @import url('https://fonts.googleapis.com/css2?family=DM+Mono:wght@500;700&family=DM+Sans:wght@400;500;600;700;800&display=swap');
      @keyframes spin { to { transform: rotate(360deg); } }
      @keyframes fadeUp {
        from { opacity: 0; transform: translateY(20px); }
        to   { opacity: 1; transform: translateY(0); }
      }
      @keyframes meshDrift {
        0%, 100% { transform: translate(0, 0) scale(1); }
        50% { transform: translate(2%, -2%) scale(1.08); }
      }
      @keyframes meshDrift2 {
        0%, 100% { transform: translate(0, 0) scale(1); }
        50% { transform: translate(-3%, 2%) scale(1.05); }
      }
      @keyframes pulse {
        0%, 100% { box-shadow: 0 0 0 0 rgba(99,102,241,0.4); }
        50%      { box-shadow: 0 0 0 8px rgba(99,102,241,0); }
      }
      @keyframes shake {
        0%, 100% { transform: translateX(0); }
        25% { transform: translateX(-6px); }
        75% { transform: translateX(6px); }
      }
      @keyframes successPop {
        0%   { transform: scale(0.6); opacity: 0; }
        60%  { transform: scale(1.1); opacity: 1; }
        100% { transform: scale(1); opacity: 1; }
      }
    `;
    document.head.appendChild(el);
    return () => {
      clearTimeout(t);
      el.remove();
    };
  }, []);

  function handleChange(index, value) {
    const char = value
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, "")
      .slice(-1);
    const next = [...digits];
    next[index] = char;
    setDigits(next);
    if (char && index < CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  }

  function handleKeyDown(index, e) {
    if (e.key === "Backspace") {
      if (digits[index]) {
        const next = [...digits];
        next[index] = "";
        setDigits(next);
      } else if (index > 0) {
        const next = [...digits];
        next[index - 1] = "";
        setDigits(next);
        inputRefs.current[index - 1]?.focus();
      }
      e.preventDefault();
    }
    if (e.key === "Enter") submit();
  }

  function handlePaste(e) {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData("text")
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, "")
      .slice(0, CODE_LENGTH);
    const next = Array(CODE_LENGTH).fill("");
    for (let i = 0; i < pasted.length; i++) next[i] = pasted[i];
    setDigits(next);
    const focusIndex = Math.min(pasted.length, CODE_LENGTH - 1);
    inputRefs.current[focusIndex]?.focus();
  }

  async function submit() {
    const code = digits.join("");
    if (code.length < CODE_LENGTH) {
      setErrorMsg("Please enter the full code.");
      setStatus("error");
      return;
    }
    setStatus("submitting");
    setErrorMsg("");
    try {
      const res = await fetch(`${API_BASE}/dashboards/tv/claim`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pairingCode: code }),
      });
      const d = await res.json();
      if (!d.success) {
        setErrorMsg(d.message || "Invalid or expired code. Try again.");
        setStatus("error");
        setDigits(Array(CODE_LENGTH).fill(""));
        inputRefs.current[0]?.focus();
        return;
      }
      if (d.data.tvId) localStorage.setItem("tvId", d.data.tvId);
      setStatus("paired");
      setTimeout(() => {
        window.location.href = `/tv/${d.data.shareToken}?tvId=${d.data.tvId}`;
      }, 1200);
    } catch (err) {
      console.error("TV claim error:", err);
      setErrorMsg("Network error. Check your connection and try again.");
      setStatus("error");
    }
  }

  const isComplete = digits.every(Boolean);
  const isSubmitting = status === "submitting";

  if (status === "paired") {
    return (
      <div style={styles.root}>
        <Ambient />
        <div style={{ ...styles.card, animation: "successPop 0.6s ease both" }}>
          <div style={{ ...styles.successIcon }}>✓</div>
          <div style={styles.title}>Paired</div>
          <div style={styles.subtitle}>Loading your dashboard…</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ ...styles.root, opacity: mounted ? 1 : 0 }}>
      <Ambient />
      <div
        style={{
          ...styles.card,
          animation: status === "error" ? "shake 0.4s" : "fadeUp 0.7s ease",
        }}
      >
        <div style={styles.iconWrap}>
          <div style={styles.iconGlow} />
          <div style={styles.icon}>📺</div>
        </div>
        <div style={styles.title}>Enter pairing code</div>
        <div style={styles.subtitle}>
          Generate a code in your dashboard under <strong>Send to TV</strong>,
          then enter it below.
        </div>

        <div style={styles.codeRow} onPaste={handlePaste}>
          {digits.map((digit, i) => (
            <input
              key={i}
              ref={(el) => (inputRefs.current[i] = el)}
              style={{
                ...styles.cell,
                borderColor: digit
                  ? "#818cf8"
                  : status === "error"
                    ? "#ef4444"
                    : "rgba(255,255,255,0.1)",
                color: "#f1f5f9",
                background: digit
                  ? "rgba(129,140,248,0.08)"
                  : "rgba(255,255,255,0.03)",
                boxShadow: digit
                  ? "0 0 24px rgba(129,140,248,0.25), inset 0 1px 0 rgba(255,255,255,0.05)"
                  : "inset 0 1px 0 rgba(255,255,255,0.04)",
                transform: digit ? "translateY(-2px)" : "translateY(0)",
              }}
              type="text"
              inputMode="text"
              maxLength={1}
              value={digit}
              autoFocus={i === 0}
              autoCapitalize="characters"
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              disabled={isSubmitting}
              onChange={(e) => handleChange(i, e.target.value)}
              onKeyDown={(e) => handleKeyDown(i, e)}
              onFocus={(e) => e.target.select()}
            />
          ))}
        </div>

        {status === "error" && (
          <div style={styles.errorMsg}>
            <span style={{ marginRight: 6 }}>⚠</span>
            {errorMsg}
          </div>
        )}

        <button
          style={{
            ...styles.btn,
            opacity: isComplete && !isSubmitting ? 1 : 0.4,
            cursor: isComplete && !isSubmitting ? "pointer" : "not-allowed",
            animation:
              isComplete && !isSubmitting ? "pulse 2s infinite" : "none",
          }}
          disabled={!isComplete || isSubmitting}
          onClick={submit}
        >
          {isSubmitting ? (
            <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={styles.spinner} />
              Pairing…
            </span>
          ) : (
            "Pair this screen"
          )}
        </button>

        <div style={styles.hint}>Codes expire 10 minutes after generation.</div>
      </div>
    </div>
  );
}

function Ambient() {
  return (
    <>
      <div
        style={{
          position: "absolute",
          top: "-20%",
          left: "-10%",
          width: "60%",
          height: "60%",
          background:
            "radial-gradient(circle, rgba(129,140,248,0.25) 0%, transparent 60%)",
          filter: "blur(40px)",
          animation: "meshDrift 16s ease-in-out infinite",
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "absolute",
          bottom: "-20%",
          right: "-10%",
          width: "55%",
          height: "55%",
          background:
            "radial-gradient(circle, rgba(167,139,250,0.18) 0%, transparent 60%)",
          filter: "blur(50px)",
          animation: "meshDrift2 20s ease-in-out infinite",
          pointerEvents: "none",
        }}
      />
    </>
  );
}

const styles = {
  root: {
    width: "100vw",
    height: "100vh",
    background: "#111827",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: "'DM Sans', Inter, system-ui, sans-serif",
    transition: "opacity 0.6s ease",
    position: "relative",
    overflow: "hidden",
  },
  card: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 24,
    padding: "56px 72px",
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.07)",
    borderRadius: 28,
    maxWidth: 640,
    width: "90vw",
    backdropFilter: "blur(20px) saturate(150%)",
    WebkitBackdropFilter: "blur(20px) saturate(150%)",
    boxShadow:
      "0 24px 64px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.06)",
    position: "relative",
    zIndex: 1,
  },
  iconWrap: {
    position: "relative",
    width: 80,
    height: 80,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  iconGlow: {
    position: "absolute",
    inset: 0,
    background:
      "radial-gradient(circle, rgba(129,140,248,0.4) 0%, transparent 70%)",
    filter: "blur(16px)",
  },
  icon: {
    fontSize: "3.5rem",
    position: "relative",
    zIndex: 1,
  },
  successIcon: {
    fontSize: "3rem",
    width: 80,
    height: 80,
    borderRadius: "50%",
    background:
      "linear-gradient(135deg, rgba(52,211,153,0.2), rgba(16,185,129,0.1))",
    border: "2px solid rgba(52,211,153,0.5)",
    color: "#34d399",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 700,
    boxShadow: "0 0 40px rgba(52,211,153,0.4)",
  },
  title: {
    fontSize: "2rem",
    fontWeight: 800,
    color: "#f1f5f9",
    textAlign: "center",
    margin: 0,
    letterSpacing: "-0.02em",
  },
  subtitle: {
    fontSize: "1rem",
    color: "rgba(148,163,184,0.85)",
    textAlign: "center",
    maxWidth: 380,
    lineHeight: 1.6,
    margin: 0,
  },
  codeRow: {
    display: "flex",
    gap: 12,
  },
  cell: {
    width: 64,
    height: 80,
    fontSize: "2rem",
    fontWeight: 800,
    textAlign: "center",
    textTransform: "uppercase",
    border: "2px solid",
    borderRadius: 14,
    outline: "none",
    caretColor: "transparent",
    transition:
      "border-color 0.25s ease, background 0.25s ease, box-shadow 0.25s ease, transform 0.25s cubic-bezier(0.34,1.56,0.64,1)",
    fontVariantNumeric: "tabular-nums",
    fontFamily: "'DM Mono', monospace",
  },
  errorMsg: {
    fontSize: "0.9rem",
    color: "#f87171",
    textAlign: "center",
    fontWeight: 500,
  },
  btn: {
    padding: "14px 48px",
    background: "linear-gradient(135deg, #818cf8, #6366f1)",
    color: "#fff",
    border: "none",
    borderRadius: 14,
    fontSize: "1rem",
    fontWeight: 700,
    transition: "all 0.2s ease",
    letterSpacing: "0.01em",
    boxShadow: "0 8px 24px rgba(99,102,241,0.35)",
  },
  spinner: {
    width: 14,
    height: 14,
    border: "2px solid rgba(255,255,255,0.3)",
    borderTopColor: "#fff",
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
    display: "inline-block",
  },
  hint: {
    fontSize: "0.82rem",
    color: "rgba(100,116,139,0.85)",
    fontWeight: 500,
  },
};
